import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { TrashIcon, PencilIcon, EyeIcon } from "@heroicons/react/24/outline";
import EmptyState from "../../../components/EmptyState";
import Drawer from "../../../blocks/drawer";
import { useStore } from "../../../contexts/StoreContext";
import { getMarketingTimeZone } from "../../../utils/marketingTimeZone";
import { timeZoneLabel } from "../../../utils/timeZones";
import { formatInZone } from "../../../utils/zonedTime";

/**
 * Component for displaying the marketing messages in a table
 * Extracted from the Marketing page for better organization and reusability
 * Uses React.memo for performance optimization
 */
const MessageTable = ({ messages, t, onEdit, onDelete }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  // Same zone the form enters times in (MessageModal), named in the header.
  const { selectedStore } = useStore();
  const storeTimeZone = selectedStore?.time_zone;
  const { timeZone } = useMemo(
    () => getMarketingTimeZone({ time_zone: storeTimeZone }),
    [storeTimeZone],
  );
  const zoneName = timeZoneLabel(timeZone);

  if (!messages || messages.length === 0) {
    return <EmptyState />;
  }

  return (
    <>
      <table className="min-w-full divide-y divide-gray-300 text-sm text-left whitespace-nowrap">
        <thead className="bg-gray-50">
          <tr className="font-semibold text-gray-900">
            <th scope="col" className="p-3 pl-4 w-1/6">
              {t("marketing.table.pass")}
            </th>
            <th scope="col" className="p-3 w-1/4">
              {t("marketing.table.scheduledAt")}
              <span className="block text-xs font-normal text-gray-500">
                {zoneName}
              </span>
            </th>
            <th scope="col" className="p-3">
              {t("marketing.table.message")}
            </th>
            <th scope="col" className="p-3">
              {t("marketing.table.messageType")}
            </th>
            <th scope="col" className="p-3">
              {t("marketing.table.from")}
            </th>
            <th scope="col" className="p-3">
              {t("marketing.table.status")}
            </th>
            <th scope="col"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white whitespace-nowrap">
          {messages.map((message) => (
            <tr key={message.id} className="text-gray-500">
              <td className="p-3 pl-4">
                {message.pass
                  ? `${message.pass.pass.name} - ${message.pass.pass.type}`
                  : t("marketing.filter.allPasses")}
              </td>
              <td className="p-3 pl-4">
                {formatInZone(message.scheduled_at, timeZone)}
              </td>
              <td className="p-3 pl-4 whitespace-normal">
                {message.message_type === "email" ? (
                  <div
                    className="prose prose-sm max-w-none"
                    title={message.message}
                  >
                    {message.message.replace(/<[^>]*>/g, "").substring(0, 20)}
                    {message.message.replace(/<[^>]*>/g, "").length > 20
                      ? "..."
                      : ""}
                  </div>
                ) : (
                  <div title={message.message}>
                    {message.message.substring(0, 20)}
                    {message.message.length > 20 ? "..." : ""}
                  </div>
                )}
              </td>
              <td className="p-3 pl-4">
                {message.message_type
                  ? message.message_type.charAt(0).toUpperCase() +
                    message.message_type.slice(1)
                  : "Sms"}
              </td>
              <td className="p-3 pl-4">
                {message.from ? (
                  <div title={message.from}>
                    {message.from.length > 20
                      ? `${message.from.substring(0, 20)}...`
                      : message.from}
                  </div>
                ) : (
                  "-"
                )}
              </td>
              <td className="p-3 pl-4">
                {message.status === "pending" && t("marketing.table.pending")}
                {message.status === "sent" && t("marketing.table.sent")}
                {message.status === "failed" && t("marketing.table.failed")}
              </td>
              <td className="relative p-3 pr-4 text-right space-x-4">
                {onEdit && message.status === "pending" && (
                  <button
                    onClick={() => onEdit(message)}
                    className="text-indigo-600 hover:text-indigo-900"
                    aria-label={`Edit message ${message.id}`}
                  >
                    <PencilIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                )}
                <button
                  onClick={() => {
                    setSelectedMessage(message);
                    setIsModalOpen(true);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                  aria-label={`View message ${message.id}`}
                >
                  <EyeIcon className="h-5 w-5" aria-hidden="true" />
                </button>
                {onDelete && (
                  <button
                    onClick={() => onDelete(message.id)}
                    className="text-red-600 hover:text-red-900"
                    aria-label={`Delete message ${message.id}`}
                  >
                    <TrashIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <Drawer
        open={isModalOpen}
        setOpen={setIsModalOpen}
        header={t("marketing.table.message")}
        onCancel={() => setIsModalOpen(false)}
        showFooter={false}
      >
        {selectedMessage && (
          <div className="px-4 py-6 sm:px-6 space-y-4">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                {t("marketing.table.pass")}
              </p>
              <p className="text-sm text-gray-900">
                {selectedMessage.pass
                  ? `${selectedMessage.pass.pass.name} - ${selectedMessage.pass.pass.type}`
                  : t("marketing.filter.allPasses")}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                {t("marketing.table.scheduledAt")}
              </p>
              <p className="text-sm text-gray-900">
                {formatInZone(selectedMessage.scheduled_at, timeZone)}{" "}
                <span className="text-gray-500">({zoneName})</span>
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                {t("marketing.table.message")}
              </p>
              {selectedMessage.message_type === "email" ? (
                <div
                  className="prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: selectedMessage.message }}
                />
              ) : (
                <p className="text-sm text-gray-900 whitespace-pre-wrap">
                  {selectedMessage.message}
                </p>
              )}
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                {t("marketing.table.messageType")}
              </p>
              <p className="text-sm text-gray-900">
                {selectedMessage.message_type
                  ? selectedMessage.message_type.charAt(0).toUpperCase() +
                    selectedMessage.message_type.slice(1)
                  : "Sms"}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                {t("marketing.table.from")}
              </p>
              <p className="text-sm text-gray-900">
                {selectedMessage.from || "-"}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                {t("marketing.table.status")}
              </p>
              <p className="text-sm text-gray-900">
                {selectedMessage.status === "pending" &&
                  t("marketing.table.pending")}
                {selectedMessage.status === "sent" && t("marketing.table.sent")}
                {selectedMessage.status === "failed" &&
                  t("marketing.table.failed")}
              </p>
            </div>
          </div>
        )}
      </Drawer>
    </>
  );
};

MessageTable.propTypes = {
  messages: PropTypes.array.isRequired,
  t: PropTypes.func.isRequired,
  // Omitted for members: the API only lets admins change messages, so the
  // table then offers viewing only.
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
};

export default React.memo(MessageTable);
